import React, { useRef } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { X, Printer, Send, RefreshCw, DollarSign, Edit2, Ban, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount, symbol = 'R') {
  return `${symbol}${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function InvoiceViewDialog({ 
  open, onOpenChange, invoice, settings,
  onSend, onResend, onEdit, onRecordPayment, onVoid
}) {
  const printRef = useRef(null);

  if (!invoice) return null;

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>Invoice ${invoice.invoice_number}</title>
      <style>body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;margin:0;padding:20px;color:#0f172a}
      table{width:100%;border-collapse:collapse}
      @media print{.no-print{display:none}}</style>
    </head><body>${content.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDownloadPdf = () => {
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}/api/billing/invoices/${invoice.id}/pdf`;
    link.target = '_blank';
    link.click();
  };

  const statusColor = {
    draft: 'bg-slate-100 text-slate-700 border-slate-300',
    sent: 'bg-blue-100 text-blue-700 border-blue-300',
    paid: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    partially_paid: 'bg-amber-100 text-amber-700 border-amber-300',
    overdue: 'bg-red-100 text-red-700 border-red-300',
    void: 'bg-slate-100 text-slate-400 border-slate-200',
  };

  const company = settings || {};
  const bankDetails = company.bank_details || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto p-0" data-testid="invoice-view-dialog">
        {/* Action bar */}
        <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between" data-testid="invoice-actions-bar">
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded text-xs font-semibold uppercase border ${statusColor[invoice.status] || statusColor.draft}`}>
              {invoice.status?.replace('_', ' ')}
            </span>
            <span className="text-sm text-slate-500 font-mono">{invoice.invoice_number}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-invoice-btn"><Printer className="h-3.5 w-3.5 mr-1" />Print</Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="download-invoice-pdf-btn"><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
            {invoice.status === 'draft' && (
              <>
                <Button variant="outline" size="sm" onClick={() => onEdit?.(invoice)} data-testid="edit-invoice-btn"><Edit2 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onSend?.(invoice)} data-testid="send-invoice-btn"><Send className="h-3.5 w-3.5 mr-1" />Send</Button>
              </>
            )}
            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
              <>
                <Button variant="outline" size="sm" onClick={() => onResend?.(invoice)} data-testid="resend-invoice-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" />Resend</Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onRecordPayment?.(invoice)} data-testid="record-payment-btn"><DollarSign className="h-3.5 w-3.5 mr-1" />Record Payment</Button>
              </>
            )}
            {invoice.status === 'partially_paid' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onRecordPayment?.(invoice)} data-testid="record-payment-btn"><DollarSign className="h-3.5 w-3.5 mr-1" />Record Payment</Button>
            )}
            {invoice.status !== 'void' && invoice.status !== 'paid' && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onVoid?.(invoice)} data-testid="void-invoice-btn"><Ban className="h-3.5 w-3.5 mr-1" />Void</Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}><X className="h-4 w-4" /></Button>
          </div>
        </div>

        {/* Invoice Content */}
        <div ref={printRef} className="p-8" data-testid="invoice-content">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <img src="/gantt-logo-blue.png" alt={company.company_name || 'Gantt'} className="h-12 mb-3" />
              <p className="text-sm text-slate-600">{company.company_name || 'Gantt Account Management'}</p>
              {company.company_address && <p className="text-sm text-slate-500">{company.company_address}</p>}
              {company.company_email && <p className="text-sm text-slate-500">{company.company_email}</p>}
              {company.company_phone && <p className="text-sm text-slate-500">{company.company_phone}</p>}
              {company.company_registration && <p className="text-xs text-slate-400 mt-1">Reg: {company.company_registration}</p>}
              {company.vat_number && <p className="text-xs text-slate-400">VAT: {company.vat_number}</p>}
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight">INVOICE</h1>
              <p className="text-lg font-mono text-slate-600 mt-1">{invoice.invoice_number}</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-slate-200">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Bill To</p>
              <p className="text-base font-semibold text-slate-900">{invoice.client_name}</p>
              {invoice.billing_address && <p className="text-sm text-slate-600">{invoice.billing_address}</p>}
              {invoice.vat_number && <p className="text-sm text-slate-500">VAT: {invoice.vat_number}</p>}
              {invoice.client_email && <p className="text-sm text-slate-500">{invoice.client_email}</p>}
            </div>
            <div className="text-right space-y-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Invoice Date</p>
                <p className="text-sm font-medium">{invoice.invoice_date}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Due Date</p>
                <p className="text-sm font-medium">{invoice.due_date}</p>
              </div>
              {invoice.billing_period_start && invoice.billing_period_end && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Service Period</p>
                  <p className="text-sm font-medium">{invoice.billing_period_start} — {invoice.billing_period_end}</p>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <table className="w-full mb-6">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest">Description</th>
                <th className="text-center py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest">Qty</th>
                <th className="text-right py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest">Unit Price</th>
                <th className="text-right py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.line_items || []).map((item, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="py-3 px-3 text-sm">{item.description}</td>
                  <td className="py-3 px-3 text-sm text-center">{item.quantity}</td>
                  <td className="py-3 px-3 text-sm text-right">{formatCurrency(item.unit_price)}</td>
                  <td className="py-3 px-3 text-sm text-right font-medium">{formatCurrency(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-72 space-y-1">
              <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Subtotal</span><span className="font-medium">{formatCurrency(invoice.subtotal)}</span></div>
              {invoice.discount_amount > 0 && (
                <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Discount</span><span className="font-medium text-red-600">-{formatCurrency(invoice.discount_amount)}</span></div>
              )}
              {invoice.include_vat !== false && invoice.tax_rate > 0 && (
                <div className="flex justify-between text-sm py-1"><span className="text-slate-500">VAT ({invoice.tax_rate}%)</span><span className="font-medium">{formatCurrency(invoice.tax_amount)}</span></div>
              )}
              {invoice.include_vat === false && (
                <div className="flex justify-between text-sm py-1 text-slate-400"><span>VAT</span><span>N/A</span></div>
              )}
              <div className="flex justify-between text-base font-bold pt-2 border-t-2 border-slate-900"><span>Total</span><span>{formatCurrency(invoice.total)}</span></div>
              {invoice.amount_paid > 0 && (
                <>
                  <div className="flex justify-between text-sm py-1 text-emerald-600"><span>Paid</span><span>-{formatCurrency(invoice.amount_paid)}</span></div>
                  <div className="flex justify-between text-base font-bold text-red-600"><span>Balance Due</span><span>{formatCurrency(invoice.balance_due)}</span></div>
                </>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Notes</p>
              <p className="text-sm text-slate-700">{invoice.notes}</p>
            </div>
          )}

          {/* Bank Details */}
          {bankDetails && (
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-6">
              <p className="text-[10px] font-bold uppercase tracking-widest text-sky-600 mb-1">Banking Details</p>
              <p className="text-sm text-sky-900 whitespace-pre-line">{bankDetails}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceViewDialog;
